import React, { useState } from 'react';
import { Key, Link, Copy, CheckCircle, Terminal } from 'lucide-react';

const DeveloperAPI = () => {
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  
  const tenantId = localStorage.getItem('tenantId') || '1';
  const apiKey = `sk_live_${tenantId}_${Math.random().toString(36).substr(2, 10)}`; // Dummy for now
  const webhookUrl = `${import.meta.env.VITE_API_URL || window.location.origin}/api/webhooks/zapier/catch`;

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Terminal className="w-6 h-6 text-gold-400" />
          Developer & API
        </h1>
        <p className="text-gray-400 text-sm mt-1">Connect ArtinSmartRealty to Zapier, Make.com, or your own custom forms.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Keys */}
        <div className="glass-card p-6 space-y-4">
          <div className="flex items-center gap-3 border-b border-white/10 pb-3">
            <div className="p-2 bg-navy-800 rounded-lg">
              <Key className="w-5 h-5 text-gold-400" />
            </div>
            <h2 className="text-lg font-semibold text-white">Your API Key</h2>
          </div>
          
          <p className="text-sm text-gray-400">Use this key to authenticate external requests to the API.</p>
          
          <div className="bg-navy-900 border border-white/10 p-4 rounded-lg flex items-center justify-between">
            <code className="text-sm text-white font-mono break-all">{apiKey}</code>
            <button 
              onClick={() => copyToClipboard(apiKey, setCopiedKey)}
              className="ml-4 p-2 bg-navy-800 hover:bg-navy-700 text-gray-400 hover:text-white rounded transition-colors"
            >
              {copiedKey ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-red-400">Keep this key secret. Do not expose it in client-side code.</p>
        </div>

        {/* Webhooks */}
        <div className="glass-card p-6 space-y-4">
          <div className="flex items-center gap-3 border-b border-white/10 pb-3">
            <div className="p-2 bg-navy-800 rounded-lg">
              <Link className="w-5 h-5 text-gold-400" />
            </div>
            <h2 className="text-lg font-semibold text-white">Inbound Webhook (Zapier)</h2>
          </div>
          
          <p className="text-sm text-gray-400">Send HTTP POST requests with JSON payload to this URL to create leads automatically.</p>
          
          <div className="bg-navy-900 border border-white/10 p-4 rounded-lg flex items-center justify-between">
            <code className="text-sm text-white font-mono truncate">{webhookUrl}</code>
            <button 
              onClick={() => copyToClipboard(webhookUrl, setCopiedUrl)}
              className="ml-4 p-2 bg-navy-800 hover:bg-navy-700 text-gray-400 hover:text-white rounded transition-colors"
            >
              {copiedUrl ? <CheckCircle className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold text-white mb-4">How to use the Zapier/Make.com Webhook</h3>
        <div className="space-y-4 text-sm text-gray-400">
          <p>1. In Zapier, select <strong>"Webhooks by Zapier"</strong> as the action, and choose <strong>"Custom Request"</strong>.</p>
          <p>2. Set Method to <strong>POST</strong> and URL to the webhook URL above.</p>
          <p>3. Under Headers, add <code className="bg-navy-900 text-gold-400 px-2 py-1 rounded">x-api-key</code> and set its value to your API key.</p>
          <p>4. Send a JSON payload containing the lead information. At minimum, provide a name or phone/email:</p>
          <pre className="bg-navy-900 border border-white/10 p-4 rounded-lg text-green-400 font-mono overflow-x-auto">
{`{
  "name": "John Doe",
  "email": "john@example.com",
  "phone": "+971501234567"
}`}
          </pre>
          <p>Any incoming request will automatically create a new lead in your pipeline marked as "External".</p>
        </div>
      </div>
    </div>
  );
};

export default DeveloperAPI;
